import {
  Box,
  styled,
  useTheme,
  useMediaQuery,
  Typography,
} from "@mui/material";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import { getRandomColor } from "../../../utilities";
import Ratings from "../../../components/common/Ratings";
import { getRelatedProducts } from "../../../utilities/service";
import { useAuth } from "../../../hooks/AuthContext";

function SuggestionProduct({ productId }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { user } = useAuth();
  const heading = "Suggestion Product";

  useEffect(() => {
    const fetchRelatedProducts = async () => {
      try {
        if (!productId || !user?.customer_id) return;
        const customerId = user.customer_id;
        const response = await getRelatedProducts(customerId, productId);
        const allProduct = response?.data || [];
        setProducts(allProduct);
      } catch (error) {
        // console.error("Error fetching related products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedProducts();
  }, [productId, user?.customer_id]);

  const settings = {
    dots: isMobile,
    infinite: true,
    speed: 500,
    autoplay: true,
    arrows: false, // Disable arrows
    slidesToShow: 3,
    slidesToScroll: 1,
    beforeChange: (current, next) => setCurrentSlide(next),
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 768, settings: { slidesToShow: 1.5 } },
      { breakpoint: 480, settings: { slidesToShow: 1 } },
    ],
    customPaging: (i) => (
      <Box
        sx={{
          width: i === currentSlide ? "16px" : "8px",
          height: "8px",
          borderRadius: i === currentSlide ? "4px" : "50%",
          backgroundColor:
            i === currentSlide
              ? "var(--primary-red-base)"
              : "var(--neutral-300)",
          margin: "0 4px",
          transition: "all 0.5s ease",
          marginTop: "12px",
        }}
      />
    ),
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>{heading}</SectionTitle>
      </SectionHeader>
      {products.length < 3 ? (
        // Show as a simple flex layout when < 3 products
        <Box sx={{ display: "flex", gap: 2 }}>
          {products.map((item) => (
            <CakeCardItem key={item.id} item={item} />
          ))}
        </Box>
      ) : (
        // Show carousel when >= 3 products
        <SliderWrapper>
          <Slider {...settings}>
            {products.map((item) => (
              <CakeCardItem key={item.id} item={item} />
            ))}
          </Slider>
        </SliderWrapper>
      )}
    </Section>
  );
}

function CakeCardItem({ item, customer }) {
  const navigate = useNavigate();

  let discountPrice = 0;
  let originalPrice = 0;
  let discountPercent = 0;

  const variation = item?.variation?.[0] || {};
  const bulkPrice = item?.bulk?.bulk_price || [];
  const comboPrice = +item?.combo_price || 0;
  // console.log("combo".comboPrice);

  // ✅ 1. Priority: Combo Price
  if (comboPrice > 0) {
    discountPrice = comboPrice;

    // Get original price from variation
    originalPrice = +variation.egg > 0 ? +variation.egg : +variation.eggLess;

    if (originalPrice > 0 && originalPrice > comboPrice) {
      discountPercent = Math.round(
        ((originalPrice - comboPrice) / originalPrice) * 100,
      );
    }
  }

  // ✅ 2. Priority: Bulk Price
  else if (bulkPrice.length > 0) {
    const leastPriceObject = bulkPrice.reduce((min, current) => {
      const currentEffectivePrice =
        +current.egg_price > 0
          ? +current.egg_price
          : +current.eggless_price || Infinity;
      const minEffectivePrice =
        +min.egg_price > 0 ? +min.egg_price : +min.eggless_price || Infinity;

      return currentEffectivePrice < minEffectivePrice ? current : min;
    }, bulkPrice[0]);

    discountPrice =
      +leastPriceObject.egg_price > 0
        ? +leastPriceObject.egg_price
        : +leastPriceObject.eggless_price;

    originalPrice = +variation.egg > 0 ? +variation.egg : +variation.eggLess;

    if (originalPrice > 0 && originalPrice > discountPrice) {
      discountPercent = Math.round(
        ((originalPrice - discountPrice) / originalPrice) * 100,
      );
    }
  }

  // ✅ 3. Fallback: Variation Pricing
  else {
    discountPrice =
      +variation.eggOriginalPrice > 0
        ? +variation.eggOriginalPrice
        : +variation.eggLessOriginalPrice;

    originalPrice = +variation.egg > 0 ? +variation.egg : +variation.eggLess;

    discountPercent = variation?.discountPercent || 0;
  }

  const isDiscounted = originalPrice > discountPrice;

  return (
    <CakeCard
      onClick={() =>
        navigate(`/product-description/${item?.product_name}/${item?.id}`)
      }
    >
      <CardContent>
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <CakeImage
            src={item.app_image?.app_product_image}
            alt={item.product_name}
            loading="lazy"
          />

          {/* Premium product tag */}
          {(item?.is_best_seller === 1 ||
            item?.is_featured === 1 ||
            Number(item?.fast_delivery) === 1) && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                background:
                  "linear-gradient(135deg, #B8842C 0%, #D7B05B 100%)",
                color: "white",
                fontWeight: 700,
                padding: { xs: "4px 8px", sm: "5px 10px" },
                borderRadius: "0px 0px 12px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
                zIndex: 2,
                fontSize: { xs: "10px", sm: "11px" },
                whiteSpace: "nowrap",
                marginBottom: { xs: "-4px", sm: "-5px" },
                letterSpacing: "0.02em",
                boxShadow: "0 10px 18px rgba(184, 132, 44, 0.22)",
              }}
            >
              Signature Pick
            </Box>
          )}
        </Box>
        <CakeInfo>
          <CakeName>{item.product_name}</CakeName>
          <Ratings value={item?.ratings} />
          <Box
            sx={{
              display: "flex",
              gap: "12px",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {discountPrice > 0 && <Price>₹{discountPrice}</Price>}

            {isDiscounted &&
              originalPrice > 0 &&
              (comboPrice === 0 ||
                customer?.customer_discount_id !== 0 ||
                customer?.customer_discont !== null) && (
                <OriginalPrice
                  sx={{
                    textDecoration: "line-through",
                    fontSize: "15px",
                    color: "var(--neutral-500)",
                    fontWeight: 500,
                  }}
                >
                  ₹{originalPrice}
                </OriginalPrice>
              )}

            {discountPercent > 0 &&
              !(
                comboPrice > 0 &&
                (!customer?.customer_discount_id ||
                  customer?.customer_discount_id === 0) &&
                !customer?.customer_discont
              ) && <SavePercentage>{discountPercent}% off</SavePercentage>}
          </Box>
        </CakeInfo>
      </CardContent>
    </CakeCard>
  );
}

// ========== Styled Components ==========

const Section = styled("section")(({ theme }) => ({
  padding: "20px",
  margin: "0 auto",
  maxWidth: "100%",
  [theme.breakpoints.down("md")]: {
    padding: "8px",
  },
}));

const SectionHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: "16px",
  [theme.breakpoints.down("md")]: {
    marginBottom: "8px",
  },
}));

const SectionTitle = styled("h2")(({ theme }) => ({
  fontWeight: 700,
  lineHeight: "133%",
  color: "var(--neutral-black)",
  textAlign: "center",
  marginBottom: "16px",
  fontSize: "24px",
  [theme.breakpoints.down("md")]: {
    fontSize: "22px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
    marginBottom: "8px",
  },
}));

const SliderWrapper = styled("div")(({ theme }) => ({
  ".slick-slide": {
    padding: "0 10px",
  },
  ".slick-list": {
    margin: "0 -10px",
  },
}));

const CakeCard = styled("div")(({ theme }) => ({
  cursor: "pointer",
  border: "1px solid var(--neutral-300)",
  borderRadius: "16px",
  background: "white",
  overflow: "hidden",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  [theme.breakpoints.down("md")]: {
    width: "250px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    padding: "12px",
  },
}));

const CardContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "12px",
  width: "100%",
}));

const CakeImage = styled("img")(({ theme }) => ({
  width: "140px",
  objectFit: "contain",
  borderRadius: "12px",
  backgroundColor: getRandomColor(),
  [theme.breakpoints.down("md")]: {
    width: "120px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "90px",
  },
}));

const CakeInfo = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  width: "100%",
  height: "100%",
}));

const CakeName = styled("h3")(({ theme }) => ({
  margin: "0",
  fontWeight: "600",
  fontSize: "18px",
  lineHeight: "150%",
  color: "var(--neutral-800)",
  textAlign: "center",
  textTransform: "capitalize",
  overflow: "hidden",
  textOverflow: "ellipsis",
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
}));

const Price = styled(Typography)(({ theme }) => ({
  fontSize: "18px",
  fontWeight: 600,
  margin: 0,
  [theme.breakpoints.down("sm")]: {
    fontSize: "16px",
  },
}));

const OriginalPrice = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 500,
  margin: 0,
  textDecoration: "line-through",
  color: "var(--neutral-500)",
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
}));

const SavePercentage = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  fontWeight: 500,
  margin: 0,
  padding: "2px 4px",
  borderRadius: "4px",
  color: "#0cb67b",
  border: "1px solid #0cb67b",
  backgroundColor: "rgba(8, 177, 118, 0.1)",
  [theme.breakpoints.down("sm")]: {
    fontSize: "10px",
  },
}));

export default SuggestionProduct;
